import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Payment } from "@/types/payment"
import { DialogoLancamentoFormData, dialogoLancamentoFormSchema } from "../relacao-pagamentos.types"
import { formatPaymentDate } from "../helpers/relacao-pagamentos-helpers"
import { useUpdate, useUploadNF } from "../queries/relacao-pagamentos-uses"
import { useFindItemsByStep } from "@/lib/queries/orcamento-use-lib"
import { useProjetoContext } from "@/context/projeto"

export function useDialogLancamentoForm(payment: Payment | null) {
  const [fiscalDocUrl, setFiscalDocUrl] = useState<string>("")
  const [faseOrcamento, setFaseOrcamento] = useState<number>(0)
  const { mutateAsync: update } = useUpdate()

  const { projetoSelecionado } = useProjetoContext()
  const budgetId = projetoSelecionado?.budgets[0]?.id || 0

  const { data: budgetItems } = useFindItemsByStep(budgetId, faseOrcamento)
  const { mutateAsync: uploadNF } = useUploadNF()

  const form = useForm<DialogoLancamentoFormData>({
    resolver: zodResolver(dialogoLancamentoFormSchema),
    defaultValues: {
      creditor: "",
      fase_orcamento: "",
      budget_item_id: "",
      payment_date: "",
      emission_type: "",
      emission_number: "",
      emission_date: "",
      amount: "",
      observations: "",
    },
  })

  useEffect(() => {
    if (!payment) return

    setFaseOrcamento(payment.budget_step_id || 0)
    setFiscalDocUrl(payment.fiscal_note_url || "")

    form.reset({
      creditor: payment.creditorName,
      payment_date: formatPaymentDate(payment.paymentDate),
      emission_type: payment.fiscalDocType,
      emission_number: payment.fiscalDocNumber,
      emission_date: formatPaymentDate(payment.emissionDate),
      amount: payment.amount,
      fase_orcamento: String(payment.budget_step_id || ""),
      budget_item_id: payment.budget_item_id,
      observations: payment.notes ?? "",
    })
  }, [payment])

  const handleFileUpload = async (file: File | null, contratoId: number) => {
    if (!file) return

    try {
      const formData = new FormData()
      formData.append("file", file)
      const response = await uploadNF({ file: formData, contractId: contratoId })
      setFiscalDocUrl(response.url)
    } catch (error) {
      console.error("Erro ao fazer upload do documento:", error)
    }
  }

  return {
    form,
    budgetItems: budgetItems?.data || [],
    setFaseOrcamento,
    update,
    fiscalDocUrl,
    handleFileUpload
  }
}
